import { useEffect, useRef, useState } from 'react';
import { loadMercadoPago } from '@mercadopago/sdk-js';
import { useTranslation } from 'react-i18next';
import { Checkout } from '../../../common/types/checkout';
import { FormDataShape } from '../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { useApi } from '../../../common/hooks/useApi';
import { getConfig } from '../../../common/utils/get-config';
import {
  Brand,
  cvvMaxForBrand,
  formatCardNumber,
} from '../../../common/utils/card-helpers';
import { removeMask } from '../../../common/utils/remove-mask';
import { encodeBase64Browser } from '../../../common/utils/base64.utils';

export type UseMpCardSectionArgs = {
  checkout: Checkout;
  data: FormDataShape;
  onChange: (field: keyof FormDataShape, value: string) => void;
  payerEmail?: string;
};

export type UseMpCardSectionCoreReturn = {
  t: (key: string) => string;

  // estados de UI
  cardNumber: string;
  cardName: string;
  expiry: string;
  cvv: string;
  brand: Brand;
  installments: number;
  installmentList: Array<{
    installments: number;
    installment_amount: number;
    total_amount: number;
    recommended_message: string;
  }>;

  onChangeCardNumber: (value: string) => Promise<void>;
  setCardName: (value: string) => void;
  onChangeExpiry: (value: string) => void;
  onChangeCvv: (value: string) => void;
  setInstallments: (value: number) => void;

  createToken: () => Promise<any | null>;
  getPayload: () => any | null;
};

export function useMpCardSectionCore(
  args: UseMpCardSectionArgs,
): UseMpCardSectionCoreReturn {
  const { checkout, data, onChange, payerEmail } = args;

  const api = useApi();
  const { t } = useTranslation();

  const mpRef = useRef<any>(null);
  const lastPayloadRef = useRef<any>(null);
  const lastBinRef = useRef<string>('');
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqNonceRef = useRef(0);

  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardNameState] = useState(data?.cardName ?? '');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [brand, setBrand] = useState<Brand>('default');

  const [installments, setInstallmentsState] = useState<number>(1);
  const [installmentList, setInstallmentList] = useState<
    UseMpCardSectionCoreReturn['installmentList']
  >([]);

  useEffect(() => {
    (async () => {
      await loadMercadoPago();
      const MP = (window as any).MercadoPago;
      if (!MP) throw new Error('SDK Mercado Pago não disponível.');
      mpRef.current = new MP(getConfig('REACT_APP_MP_PUBLIC_KEY'), {
        locale: 'pt-BR',
      });
    })().catch(console.error);
  }, []);

  const createToken = async () => {
    if (!mpRef.current) throw new Error('SDK Mercado Pago não inicializada.');

    const [mmRaw, yyRaw] = expiry.split('/');
    const cardExpirationMonth = (mmRaw || '').padStart(2, '0');
    let year = (yyRaw || '').trim();
    if (year.length === 2) {
      const century = String(new Date().getFullYear()).slice(0, 2);
      year = `${century}${year}`;
    }
    const cardExpirationYear = year;

    const tokenResp = await mpRef.current.createCardToken({
      cardNumber: cardNumber.replace(/\s/g, ''),
      cardholderName: cardName,
      cardExpirationMonth,
      cardExpirationYear,
      securityCode: cvv,
      identificationType: 'CPF',
      identificationNumber: removeMask(data?.cpf),
    });

    const token = tokenResp?.id || tokenResp?.token || tokenResp?.cardTokenId;
    if (!token) throw new Error('Não foi possível gerar o token do cartão.');

    const payload = {
      token,
      issuer_id: null as string | null,
      payment_method_id: null as string | null,
      transaction_amount: Number(checkout?.valor ?? 0),
      installments: Number(installments ?? 1),
      description: 'Descrição do produto',
      payer: {
        email: payerEmail,
        identification: {
          type: 'CPF',
          number: removeMask(data?.cpf),
        },
      },
    };

    lastPayloadRef.current = payload;
    return payload;
  };

  const getPayload = () => lastPayloadRef.current ?? null;

  const fetchInstallments = async (bin: string) => {
    if (!bin || bin.length !== 6) {
      setInstallmentList([]);
      return;
    }
    const amount = Number(checkout?.valor ?? 0);
    if (!amount) {
      setInstallmentList([]);
      return;
    }

    const prevSelected = installments;
    const myNonce = ++reqNonceRef.current;

    try {
      const { data } = await api.Venda.getInstallments({ amount, bin });
      if (reqNonceRef.current !== myNonce) return;

      const list = Array.isArray(data) ? data : [];
      setInstallmentList(list);
      const next = chooseInstallments(list, prevSelected);
      setInstallmentsState(next);
    } catch {
      if (reqNonceRef.current !== myNonce) return;
      setInstallmentList([]);
    }
  };

  const onChangeCardNumber = async (value: string) => {
    const { formatted, brand: b, rawDigits } = formatCardNumber(value);
    setCardNumber(formatted);
    setBrand(b);
    onChange('cardNumber', rawDigits);

    const maxCvv = cvvMaxForBrand(b);
    if (cvv.length > maxCvv) setCvv(cvv.slice(0, maxCvv));

    const bin = rawDigits.slice(0, 6);
    if (bin.length === 6) {
      lastBinRef.current = bin;
      await fetchInstallments(bin);
    } else {
      lastBinRef.current = '';
      setInstallmentList([]);
    }
  };

  function chooseInstallments(
    list: Array<{ installments: number }>,
    prev: number,
  ): number {
    if (!list?.length) return prev || 1;
    if (list.some((o) => o.installments === prev)) return prev;
    return list[0].installments;
  }

  useEffect(() => {
    onChange('cardName', cardName);
  }, [cardName, onChange]);

  useEffect(() => {
    const bin = lastBinRef.current;
    if (!bin) return;

    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      fetchInstallments(bin);
    }, 200);

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [checkout?.valor]);

  const onChangeExpiry = (value: string) => {
    let v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
    setExpiry(v);
    onChange('cardExpiry', v);
  };

  const onChangeCvv = (value: string) => {
    const only = value.replace(/\D/g, '');
    const max = cvvMaxForBrand(brand);
    const next = only.slice(0, max);
    const b64 = encodeBase64Browser(next);
    setCvv(next);
    onChange('cvvCardId', b64);
    onChange('cardCvv', next);
  };

  const setCardName = (val: string) => setCardNameState(val);
  const setInstallments = (val: number) => setInstallmentsState(val);

  return {
    t,
    cardNumber,
    cardName,
    expiry,
    cvv,
    brand,
    installments,
    installmentList,
    onChangeCardNumber,
    setCardName,
    onChangeExpiry,
    onChangeCvv,
    setInstallments,
    createToken,
    getPayload,
  };
}
